import React, {useState, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {bankPaymentOrderGetAll} from '../../../redux/slices/bankpaymentorder.slice'
import {basicLookup} from '../../../redux/slices/lookup.slice'

import {
  useMemoizedHandlers,
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectBankPaymentOrderData = (state) => ({
  bankPaymentOrder: state.bankpaymentorder.bankPaymentOrder,
  loading: state.bankpaymentorder.loading
})

const selectLookupData = (state) => ({
  PaymentRequestState: state.lookup.PaymentRequestState
})

function List() {
  const intl = useIntl()
  const {bankPaymentOrder, loading} = useSelector(selectBankPaymentOrderData)
  const {PaymentRequestState} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'BANKPAYMENTORDER'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(bankPaymentOrderGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(basicLookup('PaymentRequestState'))
  }, [dispatch])

  const handlers = useMemoizedHandlers({
    handleSorting: (item) => {
      setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
    },
    handleSearch: (item) => {
      setParams((prevState) => ({...prevState, name: item}))
    },
    handlePagination: (pagination) => {
      setParams((prevState) => ({...prevState, ...pagination}))
    },
    handleFilterSubmit: (values) => {
      setParams({...values})
    }
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'BANKPAYMENTORDER.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'BANKPAYMENTORDER.SEARCH.PAYMENTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'paymentDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'BANKPAYMENTORDER.SEARCH.BANKPAYMENTORDERSTATENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'bankPaymentOrderStateName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'BANKPAYMENTORDER.SEARCH.REQUESTEDPAYMENT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'requestedPayment',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'BANKPAYMENTORDER.SEARCH.APPROVEDPAYMENT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'approvedPayment',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
  ], [intl, handlers.handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'BANKPAYMENTORDER.SEARCH.BEGINDATE',
          type: 'date',
          name: 'beginDate',
        },
        {
          title: 'BANKPAYMENTORDER.SEARCH.ENDDATE',
          type: 'date',
          name: 'endDate',
        },
        {
          title: 'BANKPAYMENTORDER.SEARCH.BANKPAYMENTORDERSTATENAME',
          data: PaymentRequestState,
          name: 'bankPaymentOrderStateId',
        },
      ],
    },
  ], [PaymentRequestState])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.CASH.BANKPAYMENTORDER'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handlers.handleSearch}
          actionButtons={actionButtons}
          onSubmit={handlers.handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!bankPaymentOrder && <ListLoading />}
          {bankPaymentOrder && (
            <Table
              items={bankPaymentOrder}
              cols={columns}
              handlePagination={handlers.handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
